/**
 * Fila de KPIs del módulo.
 *
 * Mezcla dos orígenes distintos a propósito y los distingue en pantalla:
 *
 *  - **Del catálogo de métricas** (`/api/metrics`): brecha de activación,
 *    pendientes de implementación, clientes en riesgo y NRR. Traen su serie, su
 *    semáforo y su proveniencia reales, así que van en `KpiCard`.
 *  - **Derivados de las filas de esta pantalla**: "MRR sin un solo empleado
 *    corriendo", el KPI que este módulo existe para mostrar. Va en
 *    `KpiDerivado`, que publica la fórmula en vez de inventarle una fuente.
 *
 * El KPI de plata es **solo de admin**, y no porque el front lo esconda: para
 * ops y comercial el payload llega con `mrr: null` en cada cuenta, así que no
 * hay nada que sumar. El chequeo de rol es cortesía sobre un recorte que ya hizo
 * el servidor.
 */
import {
  deltaBaseOf,
  formatMetricValue,
  periodoAnterior,
  type MetricValue,
} from '@ops/contracts'
import { useState } from 'react'

import { useMetrics } from '../../lib/queries'
import { Button, KpiCard, Section } from '../../ui'
import { CargarDatoDialog, Cargando, ErrorDeCarga } from '../comunes'
import { KpiDerivado } from './KpiDerivado'
import type { TotalesPortfolio } from './derivados'

/** Métricas del catálogo que hablan del portfolio. En este orden. */
export const SLUGS_PORTFOLIO: readonly string[] = [
  'brecha_activacion_total',
  'pendientes_implementacion',
  'clientes_en_riesgo',
]

/** Solo admin la recibe: es de plata y el servidor la filtra del catálogo. */
export const SLUG_NRR = 'nrr'

export interface ClientesKpisProps {
  periodo: string
  ahora: Date
  totales: TotalesPortfolio
  /** El rol puede ver cifras de plata. */
  verPlata: boolean
  /** El rol puede cargar datos a mano. */
  puedeCargar: boolean
  /** Ir a las cuentas que facturan con cero empleados corriendo. */
  onVerEnCero: () => void
}

export function ClientesKpis({
  periodo,
  ahora,
  totales,
  verPlata,
  puedeCargar,
  onVerEnCero,
}: ClientesKpisProps) {
  const metrics = useMetrics(periodo)
  const [aCargar, setACargar] = useState<MetricValue | null>(null)

  const base = periodoAnterior(periodo)
  const valores = metrics.data?.valores ?? []
  const definiciones = metrics.data?.metricas ?? []

  const slugs = verPlata ? [...SLUGS_PORTFOLIO, SLUG_NRR] : SLUGS_PORTFOLIO
  const tarjetas = slugs
    .map((slug) => valores.find((valor) => valor.slug === slug))
    .filter((valor): valor is NonNullable<typeof valor> => valor !== undefined)

  return (
    // La fila de KPIs va dentro de una `Section` y no suelta bajo el `<h1>`: las
    // tarjetas titulan con `<h3>`, así que sin un `<h2>` antes el outline de la
    // página saltaba de 1 a 3 y los h3 quedaban precediendo al único h2, que
    // aparecía recién en la tabla de abajo.
    // El título no dice cuántas tarjetas son: ops y comercial no reciben las dos
    // de plata, así que un número cableado acá mentiría para dos de los tres roles.
    <Section titulo="El portfolio en números" espaciado="sm" regla={false}>
      <div
        data-testid="clientes-kpis"
        className="grid grid-cols-[repeat(auto-fit,minmax(224px,1fr))] gap-3"
      >
        {verPlata ? (
          <KpiDerivado
            titulo="MRR sin un solo empleado corriendo"
            valor={
              totales.mrrEnCero === null
                ? null
                : formatMetricValue(totales.mrrEnCero, 'usd')
            }
            derivacion="suma del MRR de las filas con 0 empleados instalados"
            base={`${totales.clientes} cuentas de esta vista`}
            severidad={totales.enCero.length > 0 ? 'critical' : 'ok'}
            detalleSinDato="ninguna cuenta de esta vista tiene medición de activación"
            nota={
              totales.enCero.length === 0
                ? 'Ninguna cuenta está facturando con cero empleados corriendo.'
                : `${totales.enCero.length} ${totales.enCero.length === 1 ? 'cuenta factura' : 'cuentas facturan'} sin un solo empleado instalado.`
            }
            accion={
              totales.enCero.length > 0 ? (
                <Button variante="chico" onClick={onVerEnCero}>
                  Ver esas cuentas en la brecha
                </Button>
              ) : undefined
            }
          />
        ) : null}

        {metrics.isPending ? (
          <Cargando que="los indicadores del portfolio" />
        ) : null}

        {metrics.isError ? (
          <ErrorDeCarga
            que="los indicadores del portfolio"
            error={metrics.error}
            onRetry={() => void metrics.refetch()}
            reintentando={metrics.isFetching}
          />
        ) : null}

        {tarjetas.map((metrica) => {
          const definicion = definiciones.find((d) => d.slug === metrica.slug)
          return (
            <KpiCard
              key={metrica.slug}
              metric={metrica}
              base={base === null ? null : deltaBaseOf(metrica, base)}
              direccion={definicion?.direccion}
              now={ahora}
              onLoadData={
                puedeCargar && metrica.valor === null
                  ? () => setACargar(metrica)
                  : undefined
              }
            />
          )
        })}
      </div>

      {aCargar !== null ? (
        <CargarDatoDialog
          open
          onClose={() => setACargar(null)}
          slug={aCargar.slug}
          nombre={aCargar.nombre}
          periodo={periodo}
          unidad={aCargar.unidad}
        />
      ) : null}
    </Section>
  )
}
